import {
  type ComplianceBadgeConfig,
  getComplianceBadgesForCountry,
  useUserCountry,
} from '@/components/modal/sign-in-modal/compliant-logos';
import { TypographyFootNoteBold, TypographyPBold } from '@heidi/design-system/typography';
import Image from 'next/image';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  COMPLIANCE_BADGE_MESSAGE_BY_KEY,
  trustBadgesMessages,
} from './TrustBadges.messages';

const BADGE_ICON_SIZE = 28;
const TRUSTED_CLINICIAN_COUNT = 410_000;

function TrustBadge({ badge, label }: { badge: ComplianceBadgeConfig; label: string }) {
  return (
    <div className="flex items-center gap-1.5 rounded-md p-1.5">
      <div className="flex size-7 shrink-0 items-center justify-center">
        <Image
          src={badge.iconSrc}
          alt={badge.alt}
          width={BADGE_ICON_SIZE}
          height={BADGE_ICON_SIZE}
          className="dark-invert"
        />
      </div>
      <TypographyFootNoteBold className="flex flex-col leading-none text-text-secondary">
        {label}
      </TypographyFootNoteBold>
    </div>
  );
}

export function TrustBadges() {
  const intl = useIntl();
  const country = useUserCountry();
  const badges = useMemo(() => getComplianceBadgesForCountry(country), [country]);

  return (
    <div className="flex flex-col items-center gap-2 pt-6">
      <TypographyPBold className="text-center text-text-secondary">
        <FormattedMessage
          {...trustBadgesMessages.trustedByClinicians}
          values={{ count: TRUSTED_CLINICIAN_COUNT }}
        />
      </TypographyPBold>
      <div className="flex flex-wrap items-center justify-center gap-5">
        {badges.map((badge) => {
          const messageKey = badge.text;
          const label =
            messageKey in COMPLIANCE_BADGE_MESSAGE_BY_KEY
              ? intl.formatMessage(
                  COMPLIANCE_BADGE_MESSAGE_BY_KEY[
                    messageKey as keyof typeof COMPLIANCE_BADGE_MESSAGE_BY_KEY
                  ]
                )
              : messageKey;

          return <TrustBadge key={badge.text} badge={badge} label={label} />;
        })}
      </div>
    </div>
  );
}
